import { useEffect, useState } from "react";
import axiosInstance from "../api/apiClient";

/**
 * تحديث 98 — سجل أنواع أوامر العمل: المصدر الوحيد لأسماء الأنواع وترتيبها وألوانها وأيقوناتها و«له مسار».
 * كل الشاشات تقرأ من هنا (نسخة واحدة محفوظة في الذاكرة تُحدَّث بعد أي تعديل) بدل القوائم الثابتة.
 * قبل وصول السجل تُستخدم القيم الافتراضية نفسها (الأنواع كما كانت).
 */

export const LEGACY_TYPES = ["Construction", "Maintenance", "Emergency"];

/** أيقونات السجل (المفتاح يُحفظ في السجل). */
export const TYPE_ICONS = {
  construction: "🏗", maintenance: "🛠", emergency: "⚡", rehabilitation: "🛣", private: "🏢",
  lighting: "💡", telecom: "📡", water: "🚰", civil: "🧱",
};
export const TYPE_COLORS = ["#1d4ed8", "#17723f", "#b45309", "#6a4bb0", "#0e7490", "#be185d", "#5b6b8c"];
export const typeEmoji = (icon) => TYPE_ICONS[icon] || "▣";

const DEFAULTS = [
  { code: "Construction", nameAr: "الإنشاءات", nameEn: "Construction", pathCode: "CON", icon: "construction", color: "#1d4ed8", sortOrder: 1, isActive: true, inWorkflow: true, live: true, isWorkOrder: true, isSystem: true, isLegacy: true, storageReady: true, pathReady: true, status: "active", route: "construction" },
  { code: "Maintenance", nameAr: "الصيانة", nameEn: "Maintenance", pathCode: "MNT", icon: "maintenance", color: "#17723f", sortOrder: 2, isActive: true, inWorkflow: true, live: true, isWorkOrder: true, isSystem: true, isLegacy: true, storageReady: true, pathReady: true, status: "active", route: "maintenance" },
  { code: "Emergency", nameAr: "الطوارئ", nameEn: "Emergency", pathCode: "EMR", icon: "emergency", color: "#b45309", sortOrder: 3, isActive: true, inWorkflow: true, live: true, isWorkOrder: true, isSystem: true, isLegacy: true, storageReady: true, pathReady: true, status: "active", route: "emergency" },
  { code: "NewProject", nameAr: "أعمال التأهيل", nameEn: "Rehabilitation works", pathCode: "RHB", icon: "rehabilitation", color: "#6a4bb0", sortOrder: 4, isActive: true, inWorkflow: false, live: false, isWorkOrder: true, isSystem: true, isLegacy: false, storageReady: true, pathReady: true, status: "active", route: "newproject" },
  { code: "PrivateProject", nameAr: "المشاريع الخاصة", nameEn: "Private projects", pathCode: null, icon: "private", color: "#5b6b8c", sortOrder: 5, isActive: true, inWorkflow: false, live: false, isWorkOrder: false, isSystem: true, isLegacy: false, storageReady: true, pathReady: false, status: "active", route: "privateproject" },
];

let cache = null;
let canManage = false;
let pending = null;
let seq = 0; // مراجعة 99: آخر طلب فقط يحدّث الذاكرة
const subscribers = new Set();
const publish = () => subscribers.forEach((fn) => fn(cache));

/** الأنواع الحالية (من الذاكرة) — بترتيب السجل. */
export function cachedTypes() {
  return cache || DEFAULTS;
}

export function canManageTypes() {
  return canManage;
}

export async function loadProjectTypes(force = false) {
  if (cache && !force) return cache;
  if (pending && !force) return pending;
  const mine = ++seq;
  const req = axiosInstance.get("ProjectTypes")
    .then(({ data }) => {
      // مراجعة 99: استجابة طلب أقدم (انتهى بعد تحديث قسري) لا تكتب فوق الأحدث
      if (mine !== seq) return cachedTypes();
      const list = Array.isArray(data?.types) ? data.types : [];
      if (list.length) cache = [...list].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
      canManage = !!data?.canManage;
      publish();
      return cachedTypes();
    })
    .catch(() => cachedTypes())
    .finally(() => { if (pending === req) pending = null; });
  pending = req;
  return req;
}

/** الأنواع للعرض في أي مكون — تُحدَّث تلقائياً بعد تعديل السجل. */
export function useProjectTypes() {
  const [types, setTypes] = useState(cachedTypes());
  useEffect(() => {
    const fn = (list) => setTypes(list || cachedTypes());
    subscribers.add(fn);
    loadProjectTypes().then((list) => setTypes(list));
    return () => { subscribers.delete(fn); };
  }, []);
  return types;
}

export const findType = (code) => cachedTypes().find((t) => t.code === code) || null;
export const typeLabel = (code) => findType(code)?.nameAr || DEFAULTS.find((t) => t.code === code)?.nameAr || code || "";
/** أنواع «لها مسار» (المصمم وإعدادات المسار). */
export const pathTypes = (types = cachedTypes()) => types.filter((t) => t.inWorkflow);
/** أنواع الشاشات الموحدة (الأصلية دائماً، والجديد بعد اعتماد أول مسار له). */
export const liveTypes = (types = cachedTypes()) => types.filter((t) => t.live);

// ── الإدارة ──
const refresh = () => loadProjectTypes(true);

export async function createProjectType(body) {
  const { data } = await axiosInstance.post("ProjectTypes", body);
  await refresh();
  return data;
}

export async function updateProjectType(code, body) {
  const { data } = await axiosInstance.put(`ProjectTypes/${encodeURIComponent(code)}`, body);
  await refresh();
  return data;
}

export async function deleteProjectType(code, note) {
  const { data } = await axiosInstance.delete(`ProjectTypes/${encodeURIComponent(code)}`, { params: note ? { note } : undefined });
  await refresh();
  return data;
}

export async function fetchProjectTypeChanges() {
  const { data } = await axiosInstance.get("ProjectTypes/changes");
  return Array.isArray(data) ? data : [];
}

export const errorMessage = (err, fallback) => err?.response?.data?.message || fallback;
